import type { Catalog } from "../en";

/** Filipino: platform admin app (mirrors en/admin.ts). */
export const admin: Catalog["admin"] = {
  login: {
    title: "Mag-sign in · {{product}} Admin",
    heading: "Mag-sign in sa {{product}} Admin",
    subtitle: "Para sa staff ng CRUD.IT Solutions. Kailangan ang two-factor authentication.",
    googleSso: "Magpatuloy gamit ang Google Workspace",
    googleHint: "Para lang sa mga account na @cruditsolutions.com ang Google sign-in.",
    submit: "Magpatuloy",
    tenantConsole: "Hinahanap mo ba ang workspace mo?",
    tenantConsoleLink: "Pumunta sa workspace console",
    totpHeading: "Two-factor verification",
    totpSub: "Ilagay ang 6-digit na code mula sa iyong authenticator app para sa",
    enrolHeading: "I-set up ang two-factor authentication",
    enrolSub: "Kailangan ang 2FA ng lahat ng platform staff. Idagdag ang account na ito sa iyong authenticator app, saka ilagay ang 6-digit na code na ipinapakita nito.",
    enrolStep1: "Buksan ang Google Authenticator, Microsoft Authenticator, 1Password o Authy.",
    enrolStep2: "I-scan ang QR code na ito, o ilagay nang mano-mano ang setup key. Sa device na ito, buksan na lang ang setup link.",
    enrolStep3: "Ilagay ang 6-digit na code para matapos.",
    qrLabel: "QR code para sa pag-set up ng two-factor authentication",
    setupKey: "Setup key",
    setupLink: "Buksan sa authenticator app",
    copyKey: "Kopyahin ang key",
    verify: "I-verify at mag-sign in",
    useBackup: "Gumamit ng backup code",
    useApp: "Gamitin ang iyong authenticator app",
    backupCodesTitle: "I-save ang iyong backup codes",
    backupCodesBody: "Isang beses lang gumagana ang bawat code kung mawala ang phone mo. Hindi na ito ipapakita ulit.",
    backupCodesSaved: "Na-save ko na ang backup codes",
    copyCodes: "Kopyahin ang codes",
    ipBlocked: "Hindi pinapayagang mag-sign in mula sa network na ito. Gumamit ng pinapayagang admin IP o VPN.",
    notStaff: "Hindi platform staff ang account na iyan. Gamitin ang workspace console.",
    restart: "Magsimula ulit",
  },
  shell: {
    platformCrumb: "Platform",
    roleNames: {
      super_admin: "Super Admin",
      platform_engineer: "Platform Engineer / Admin",
      sales_billing: "Sales at Billing",
      support_agent: "Support Agent",
      qa: "QA — Read-only",
    },
  },
  staff: {
    title: "Platform staff",
    subtitle: "Mga tao ng CRUD.IT Solutions na may access sa Sagot admin console. Hiwalay sa mga miyembro ng tenant team.",
    accessLog: "Access log",
    invite: "Mag-imbita ng staff",
    policyLead: "Kailangan ang 2FA ng lahat ng platform staff",
    policyBody: "(TOTP o passkey). Para lang sa mga Google Workspace account na @cruditsolutions.com ang sign-in at sa mga pinapayagang admin IP sa",
    policyLink: "Platform settings → Security",
    search: "Maghanap ng staff",
    count: "{{members}} miyembro · {{pending}} nakabinbing imbitasyon",
    count_other: "{{members}} miyembro · {{pending}} nakabinbing imbitasyon",
    colMember: "Miyembro",
    colRole: "Role",
    col2fa: "2FA",
    colLastActive: "Huling aktibo",
    colImpersonations: "Impersonation (30d)",
    colActions: "Mga aksyon",
    you: "(ikaw)",
    totp: "TOTP",
    setupPending: "Hinihintay ang setup",
    owner: "Owner",
    roleFor: "Role ni {{name}}",
    roleChanged: "{{role}} na si {{name}} · kailangang mag-sign in ulit",
    ownRole: "Hindi mo mababago ang sarili mong role",
    pendingInvite: "Mag-e-expire sa {{date}}",
    requiredFirstLogin: "Kailangan sa unang login",
    never: "Hindi pa",
    now: "Ngayon",
    noData: "—",
    emptyTitle: "Walang tugmang staff",
    emptyBody: "Subukan ang ibang pangalan o email.",
    loadError: "Hindi ma-load ang staff.",
    matrixTitle: "Mga role at permission",
    matrixSubtitle: "Ang kayang gawin ng bawat platform role. Laging may lahat ng permission ang Super Admin. Naka-log ang mga pagbabago bilang",
    matrixReadOnly: "Darating sa susunod na release ang pag-edit ng matrix.",
    permission: "Permission",
    allowed: "Pinapayagan",
    readOnly: "Read-only",
    noAccess: "Walang access",
    inviteTitle: "Mag-imbita ng platform staff",
    inviteEmail: "Email sa trabaho",
    inviteEmailPlaceholder: "pangalan@cruditsolutions.com",
    inviteEmailHint: "Mga account na @cruditsolutions.com lang ang maiimbita.",
    inviteEmailDomain: "Gumamit ng address na @cruditsolutions.com",
    inviteRole: "Role",
    require2fa: "Kailangan ang 2FA sa unang sign-in",
    alwaysOn: "Laging naka-on",
    sendInvite: "Ipadala ang imbitasyon",
    cancel: "Kanselahin",
    invited: "Naipadala ang imbitasyon · mag-e-expire sa loob ng 7 araw",
    roleDescriptions: {
      platform_engineer: "Mga model, key, kalusugan ng sistema, impersonation",
      sales_billing: "Mga tenant, plano, invoice — walang key o impersonation",
      support_agent: "Mga ticket, read-only na impersonation, abuse report",
      qa: "Nakikita ang lahat, walang binabago",
      super_admin: "Lahat ng permission, kasama ang pamamahala ng staff",
    },
    permissions: {
      tenants_view: "Tingnan ang mga tenant",
      tenants_view_desc: "Direktoryo, paggamit, kalusugan",
      tenants_impersonate: "Mag-impersonate ng tenant",
      tenants_impersonate_desc: "Laging naka-log; read-only bilang default",
      billing_manage: "Pamahalaan ang billing",
      billing_manage_desc: "Mga invoice, credit, refund (PayMongo)",
      models_manage: "Pamahalaan ang mga model at key",
      models_manage_desc: "Mga provider, routing, pag-rotate ng API key",
      plans_manage: "Pamahalaan ang mga plano",
      plans_manage_desc: "Mga plano, limitasyon, presyo",
      support_manage: "Mga support ticket at abuse",
      support_manage_desc: "Sumagot, mag-escalate, mag-suspend ng agent",
      audit_view: "Tingnan ang audit logs",
      audit_view_desc: "Audit trail ng buong platform",
      staff_manage: "Pamahalaan ang staff",
      staff_manage_desc: "Mag-imbita, magpalit ng role, mag-alis",
      settings_manage: "Settings ng platform",
      settings_manage_desc: "Branding, seguridad, feature flags",
    },
  },
  tenants: {
    title: "Mga Tenant",
    subtitle: "Bawat organisasyon sa Sagot — mga paaralan, klinika, realty, resort at LGU pilot.",
    search: "Maghanap ng pangalan o URL…",
    colTenant: "Tenant",
    colIndustry: "Industriya",
    colPlan: "Plano",
    colStatus: "Status",
    colAgents: "Mga agent",
    colMembers: "Mga miyembro",
    colMessages: "Mga mensahe (MTD)",
    colCreated: "Ginawa",
    noPlan: "Walang plano",
    trialLeft_one: "Trial — {{count}} araw na lang",
    trialLeft_other: "Trial — {{count}} araw na lang",
    statuses: {
      active: "Aktibo",
      trial: "Trial",
      past_due: "Lampas na sa takdang bayad",
      suspended: "Suspendido",
      deleted: "Binura",
    },
    unlimited: "{{used}} / custom",
    usage: "{{used}} / {{limit}}",
    showing_one: "Ipinapakita ang {{count}} tenant",
    showing_other: "Ipinapakita ang {{count}} tenant",
    loadMore: "Mag-load pa",
    emptyTitle: "Walang tenant na tugma sa mga filter",
    emptyBody: "Subukan ang ibang pangalan o i-clear ang paghahanap.",
    clear: "I-clear ang paghahanap",
    loadError: "Hindi ma-load ang mga tenant.",
    impersonationEnded: "Natapos na ang support session",
  },
};
